/**
 * Минимальная сумма заказа у поставщика: расчёт и формулировки.
 *
 * Многие поставщики везут только от определённой суммы, и узнаёт об этом закупщик в
 * момент отказа — когда заказ уже собран. Порог хранится в карточке поставщика, чтобы
 * сказать об этом до формирования бланка.
 */

export type MinOrderState =
  /** Порог задан и набран. */
  | { kind: 'met'; minAmount: number }
  /** Порог задан и не набран. */
  | { kind: 'short'; minAmount: number; missing: number }
  /** Спрашивали, поставщик не назвал — напоминать не о чем. */
  | { kind: 'unknown' }
  /** Не заполняли. Уместно предложить уточнить. */
  | { kind: 'unset' }

export const formatRubles = (value: number): string =>
  `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(value))} ₽`

export const resolveMinOrderState = (
  supplier: { minOrderAmount: number | null; minOrderAmountUnknown: boolean } | undefined,
  groupTotal: number,
): MinOrderState => {
  if (!supplier) {
    return { kind: 'unset' }
  }
  if (supplier.minOrderAmountUnknown) {
    return { kind: 'unknown' }
  }
  if (supplier.minOrderAmount == null) {
    return { kind: 'unset' }
  }
  const missing = supplier.minOrderAmount - groupTotal
  // Ровно набранная сумма считается набранной: «не хватает 0 ₽» — бессмысленная строка.
  return missing > 0
    ? { kind: 'short', minAmount: supplier.minOrderAmount, missing }
    : { kind: 'met', minAmount: supplier.minOrderAmount }
}

export const describeMinOrderState = (state: MinOrderState): string | null => {
  if (state.kind === 'short') {
    return `Минимальная сумма заказа у поставщика ${formatRubles(state.minAmount)}. Нужно добавить ещё на ${formatRubles(state.missing)}.`
  }
  if (state.kind === 'unset') {
    return 'Минимальная сумма заказа не указана. Уточните у поставщика и впишите в карточку — подскажем до отправки.'
  }
  return null
}
